import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Search, 
  Filter, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar,
  ChevronDown,
  ArrowRight,
  Tractor,
  Construction,
  Wrench,
  Tag
} from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Equipment Card Component
const EquipmentCard = ({ item }) => (
  <div 
    className="equipment-card group cursor-pointer"
    style={{ background: "var(--background-200)", border: "1px solid rgba(176, 176, 176, 0.15)", borderRadius: "1rem", overflow: "hidden" }}
  >
    <div className="aspect-video overflow-hidden relative">
      <img
        src={item.image}
        alt={item.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute top-4 left-4">
        <span 
          className="px-3 py-1 rounded-full text-xs font-semibold"
          style={{ background: "var(--primary-100)", color: "white" }}
        >
          {item.category}
        </span>
      </div>
      <div className="absolute top-4 right-4">
        <span 
          className="px-3 py-1 rounded-full text-xs font-semibold"
          style={{ background: "rgba(0,0,0,0.6)", color: "white" }}
        >
          {item.condition}
        </span>
      </div>
    </div>
    <div className="p-5">
      <h3 
        className="font-bold text-lg mb-2" 
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
      >
        {item.title}
      </h3>
      <p className="text-sm mb-3" style={{ color: "var(--accent-100)" }}>{item.specs}</p>
      
      <div className="flex items-center gap-2 mb-3 text-sm" style={{ color: "var(--text-300)" }}>
        <MapPin size={14} />
        <span>{item.location}</span>
      </div>
      
      <div className="flex items-center gap-2 mb-4 text-sm" style={{ color: "var(--text-300)" }}>
        <Calendar size={14} />
        <span>Listed {item.listedDate}</span>
      </div>
      
      <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid rgba(176, 176, 176, 0.15)" }}>
        <span className="text-xl font-bold" style={{ color: "var(--primary-300)" }}>{item.price}</span>
        <button className="btn-primary text-sm py-2 px-4">View Details</button>
      </div>
      
      <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <p className="text-xs" style={{ color: "var(--text-300)" }}>
          Dealer: <span className="font-medium" style={{ color: "var(--text-200)" }}>{item.dealer}</span>
        </p>
      </div>
    </div>
  </div>
);

// Filter Button Component
const FilterButton = ({ label, active, onClick, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
      active ? 'btn-primary' : 'btn-outline'
    }`}
    style={active ? {} : { borderColor: "rgba(176, 176, 176, 0.3)", color: "var(--text-200)" }}
  >
    {Icon && <Icon size={16} />}
    {label}
  </button>
);

// Main Used Equipment Page Component
const UsedEquipmentPage = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const equipment = [
    {
      id: 1,
      image: "https://www.machinerymarketed.com/agri-1.jpg",
      title: "2021 John Deere 6150R",
      specs: "150HP • 2,400 hrs • 4WD",
      price: "$185,000",
      category: "Agriculture",
      condition: "Excellent",
      location: "Sydney, NSW",
      listedDate: "2 days ago",
      dealer: "Premier Ag Machinery"
    },
    {
      id: 2,
      image: "https://www.machinerymarketed.com/excavator_ground.jpg",
      title: "2019 CAT 320GC",
      specs: "21T • 3,100 hrs • Tier 4",
      price: "$245,000",
      category: "Construction",
      condition: "Very Good",
      location: "Melbourne, VIC",
      listedDate: "5 days ago",
      dealer: "Civil Equipment Sales"
    },
    {
      id: 3,
      image: "https://www.machinerymarketed.com/agri-6.jpg",
      title: "2020 Case IH 9240",
      specs: "550HP • 1,800 hrs • AFS",
      price: "$520,000",
      category: "Agriculture",
      condition: "Excellent",
      location: "Brisbane, QLD",
      listedDate: "1 week ago",
      dealer: "Outback Tractors"
    },
    {
      id: 4,
      image: "https://www.machinerymarketed.com/agri-3.jpg",
      title: "2022 Komatsu WA380",
      specs: "18T • 950 hrs • Quick Hitch",
      price: "$195,000",
      category: "Construction",
      condition: "Like New",
      location: "Perth, WA",
      listedDate: "3 days ago",
      dealer: "West Coast Equipment"
    },
    {
      id: 5,
      image: "https://www.machinerymarketed.com/loader_yard.jpg",
      title: "2020 Bobcat S650",
      specs: "74HP • 1,200 hrs • High Flow",
      price: "$65,000",
      category: "Construction",
      condition: "Very Good",
      location: "Adelaide, SA",
      listedDate: "1 day ago",
      dealer: "Skid Steer Specialists"
    },
    {
      id: 6,
      image: "https://www.machinerymarketed.com/tractor_loader_pair.jpg",
      title: "2018 Kubota M7-172",
      specs: "172HP • 3,400 hrs • KVT",
      price: "$145,000",
      category: "Agriculture",
      condition: "Good",
      location: "Hobart, TAS",
      listedDate: "4 days ago",
      dealer: "Tasmanian Farm Equipment"
    },
    {
      id: 7,
      image: "https://www.machinerymarketed.com/agri-4.jpg",
      title: "2021 New Holland T7.270",
      specs: "270HP • 1,600 hrs • PLM",
      price: "$285,000",
      category: "Agriculture",
      condition: "Excellent",
      location: "Canberra, ACT",
      listedDate: "6 days ago",
      dealer: "Capital Ag Solutions"
    },
    {
      id: 8,
      image: "https://www.machinerymarketed.com/agri-7.jpg",
      title: "2019 Hitachi ZX200",
      specs: "20T • 2,800 hrs • Long Reach",
      price: "$215,000",
      category: "Construction",
      condition: "Very Good",
      location: "Darwin, NT",
      listedDate: "2 weeks ago",
      dealer: "Northern Equipment Co"
    }
  ];

  const categories = ["All", "Agriculture", "Construction", "Material Handling"];

  const filteredEquipment = equipment.filter(item => {
    const matchesCategory = activeFilter === "All" || item.category === activeFilter;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.specs.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.dealer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-16">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Equipment yard"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
              loading="eager"
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" 
              }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Tag size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Subscriber Listings</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Used <span className="text-gradient">Equipment</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto mb-8"
                style={{ color: "var(--text-200)" }}
              >
                Browse quality machinery from our subscriber dealerships. All listings are professionally appraised, 
                accurately valued, and market-ready.
              </p>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div 
                  className="flex items-center gap-2 p-2 rounded-full"
                  style={{ background: "var(--background-200)", border: "1px solid rgba(176, 176, 176, 0.2)" }}
                >
                  <Search size={20} style={{ color: "var(--text-300)", marginLeft: "1rem" }} />
                  <input
                    type="text"
                    placeholder="Search by make, model, or dealer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none"
                    style={{ color: "var(--text-100)" }}
                  />
                  <button className="btn-primary rounded-full px-6 py-2">
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filters Section */}
        <section className="py-6" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium mr-2" style={{ color: "var(--text-300)" }}>
                  Filter by:
                </span>
                {categories.map(category => (
                  <FilterButton
                    key={category}
                    label={category}
                    active={activeFilter === category}
                    onClick={() => setActiveFilter(category)}
                    icon={category === "Agriculture" ? Tractor : category === "Construction" ? Construction : category === "Material Handling" ? Wrench : Filter}
                  />
                ))}
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-sm"
                style={{ color: "var(--primary-300)" }}
              >
                Advanced Filters
                <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {showFilters && (
              <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(176, 176, 176, 0.15)" }}>
                <div className="grid md:grid-cols-4 gap-4">
                  <select className="rounded-lg p-2 text-sm" style={{ background: "var(--background-300)", color: "var(--text-200)", border: "1px solid rgba(176, 176, 176, 0.2)" }}>
                    <option>Price Range</option>
                    <option>Under $100k</option>
                    <option>$100k - $250k</option>
                    <option>$250k - $500k</option>
                    <option>Over $500k</option>
                  </select>
                  <select className="rounded-lg p-2 text-sm" style={{ background: "var(--background-300)", color: "var(--text-200)", border: "1px solid rgba(176, 176, 176, 0.2)" }}>
                    <option>Hours</option>
                    <option>Under 1,000 hrs</option>
                    <option>1,000 - 2,500 hrs</option>
                    <option>2,500 - 5,000 hrs</option>
                    <option>Over 5,000 hrs</option>
                  </select>
                  <select className="rounded-lg p-2 text-sm" style={{ background: "var(--background-300)", color: "var(--text-200)", border: "1px solid rgba(176, 176, 176, 0.2)" }}>
                    <option>Year</option>
                    <option>2023+</option>
                    <option>2020-2022</option>
                    <option>2017-2019</option>
                    <option>2016 & Older</option>
                  </select>
                  <select className="rounded-lg p-2 text-sm" style={{ background: "var(--background-300)", color: "var(--text-200)", border: "1px solid rgba(176, 176, 176, 0.2)" }}>
                    <option>Location</option>
                    <option>NSW</option>
                    <option>VIC</option>
                    <option>QLD</option>
                    <option>WA</option>
                    <option>SA</option>
                    <option>TAS</option>
                    <option>ACT</option>
                    <option>NT</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Equipment Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 
                className="text-2xl font-bold"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                {filteredEquipment.length} Machines Available
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: "var(--text-300)" }}>Sort by:</span>
                <select 
                  className="rounded-lg p-2 text-sm"
                  style={{ background: "var(--background-200)", color: "var(--text-200)", border: "1px solid rgba(176, 176, 176, 0.2)" }}
                >
                  <option>Newest Listed</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Hours: Low to High</option>
                  <option>Year: Newest First</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredEquipment.map(item => (
                <EquipmentCard key={item.id} item={item} />
              ))}
            </div>

            {filteredEquipment.length === 0 && (
              <div className="text-center py-16">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: "var(--background-200)" }}
                >
                  <Search size={32} style={{ color: "var(--text-300)" }} />
                </div>
                <h3 
                  className="text-xl font-bold mb-2"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                >
                  No equipment found
                </h3>
                <p style={{ color: "var(--text-300)" }}>
                  Try adjusting your filters or search terms
                </p>
              </div>
            )}

            {/* Pagination */}
            {filteredEquipment.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button className="btn-outline px-4 py-2 text-sm rounded-lg" disabled>
                  Previous
                </button>
                <button className="btn-primary px-4 py-2 text-sm rounded-lg">
                  1
                </button>
                <button className="btn-outline px-4 py-2 text-sm rounded-lg">
                  2
                </button>
                <button className="btn-outline px-4 py-2 text-sm rounded-lg">
                  3
                </button>
                <span style={{ color: "var(--text-300)" }}>...</span>
                <button className="btn-outline px-4 py-2 text-sm rounded-lg">
                  Next
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden max-w-4xl mx-auto"
            >
              {/* Background */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  background: "linear-gradient(135deg, var(--primary-100) 0%, var(--primary-300) 100%)"
                }}
              />
              
              {/* Content */}
              <div className="relative z-10 p-12 text-center">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white" }}
                >
                  Want to List Your Equipment?
                </h2>
                <p className="mb-8 text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                  Join our subscriber dealerships and get your machines in front of thousands of buyers.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link 
                    to="/#contact" 
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "white", 
                      color: "var(--primary-100)"
                    }}
                  >
                    Become a Subscriber <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <div className="flex items-center gap-4">
              <a href="tel:+61497085157" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-300)" }}>
                <Phone size={16} />
                +61 497 085 157
              </a>
              <a href="mailto:control@machinerymarketed.com" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-300)" }}>
                <Mail size={16} />
                Email Us
              </a>
            </div>
          </div>

          <div className="mt-8 pt-8 text-center" style={{ borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved. All listings are from verified subscriber dealerships.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UsedEquipmentPage;
